import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Warning, Clock, ArrowSquareOut } from "@phosphor-icons/react";

function Section({ title, items, color, emptyMessage, columns, renderRow }) {
  return (
    <div className="border border-slate-200 bg-white mb-8">
      <div className={`px-6 py-4 border-b border-slate-200 flex items-center justify-between ${color}`}>
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2">{title}</h2>
        <div className="text-xs font-mono tracking-[0.15em] uppercase">{items.length}</div>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-sm text-slate-500">{emptyMessage}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
              {columns.map((c) => <th key={c.k} className={`px-6 py-2 ${c.right ? "text-right" : ""}`}>{c.label}</th>)}
              <th className="px-6 py-2 w-12"></th>
            </tr>
          </thead>
          <tbody>{items.map(renderRow)}</tbody>
        </table>
      )}
    </div>
  );
}

export default function AlertsPage() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/dashboard/stats").then(({ data }) => setStats(data)); }, []);
  if (!stats) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-8 max-w-[1300px]">
      <header className="mb-8">
        <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Operations</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <Warning size={32} weight="fill" className="text-amber-600" /> Alerts
        </h1>
      </header>

      <Section
        title={<><Warning size={18} weight="fill" className="text-red-600" /> Low Stock</>}
        items={stats.low_stock}
        color="bg-red-50"
        emptyMessage="All products above their threshold."
        columns={[
          { k: "name", label: "Product" },
          { k: "type", label: "Type" },
          { k: "sku", label: "Base SKU" },
          { k: "remaining", label: "Remaining", right: true },
          { k: "threshold", label: "Threshold", right: true },
        ]}
        renderRow={(r) => {
          const isCount = r.inventory_type === "count";
          const remaining = isCount ? `${r.remaining_count} units` : `${(r.remaining_meters ?? 0).toFixed(2)}m`;
          const threshold = isCount ? `${r.threshold} units` : `${r.threshold}m`;
          return (
            <tr key={r.product_id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="px-6 py-3 font-medium">{r.name}</td>
              <td className="px-6 py-3">
                <span className={`text-[9px] font-mono tracking-[0.15em] uppercase px-1.5 py-0.5 rounded-sm border ${
                  isCount ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue-50 text-blue-700 border-blue-200"
                }`}>{isCount ? "Count" : "Rolls"}</span>
              </td>
              <td className="px-6 py-3 font-mono text-xs">{r.base_sku}</td>
              <td className="px-6 py-3 font-mono text-right tabular-nums text-red-600 font-semibold">{remaining}</td>
              <td className="px-6 py-3 font-mono text-right tabular-nums">{threshold}</td>
              <td className="px-6 py-3"></td>
            </tr>
          );
        }}
      />

      <Section
        title={<><Clock size={18} weight="fill" className="text-red-600" /> Expired</>}
        items={stats.expired}
        color="bg-red-50"
        emptyMessage="No expired rolls."
        columns={[
          { k: "sku", label: "SKU" },
          { k: "batch", label: "Batch" },
          { k: "expired", label: "Expired" },
          { k: "rem", label: "Remaining", right: true },
        ]}
        renderRow={(r) => (
          <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
            <td className="px-6 py-3 font-mono text-xs">{r.sku}</td>
            <td className="px-6 py-3 font-mono text-xs">{r.batch_number}</td>
            <td className="px-6 py-3 font-mono text-xs text-red-600">{r.expiry_date} ({-r.days_until_expiry}d ago)</td>
            <td className="px-6 py-3 font-mono text-right tabular-nums">{r.current_length_m.toFixed(2)}m</td>
            <td className="px-6 py-3 text-right">
              <Link to={`/rolls/${r.id}`} className="text-blue-600 inline-flex items-center gap-1"><ArrowSquareOut size={14} /></Link>
            </td>
          </tr>
        )}
      />

      <Section
        title={<><Clock size={18} weight="fill" className="text-amber-600" /> Expiring within 90 days</>}
        items={stats.expiring_soon}
        color="bg-amber-50"
        emptyMessage="No rolls expiring within 90 days."
        columns={[
          { k: "sku", label: "SKU" },
          { k: "batch", label: "Batch" },
          { k: "exp", label: "Expires" },
          { k: "rem", label: "Remaining", right: true },
        ]}
        renderRow={(r) => (
          <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
            <td className="px-6 py-3 font-mono text-xs">{r.sku}</td>
            <td className="px-6 py-3 font-mono text-xs">{r.batch_number}</td>
            <td className="px-6 py-3 font-mono text-xs text-amber-600">{r.expiry_date} ({r.days_until_expiry}d)</td>
            <td className="px-6 py-3 font-mono text-right tabular-nums">{r.current_length_m.toFixed(2)}m</td>
            <td className="px-6 py-3 text-right">
              <Link to={`/rolls/${r.id}`} className="text-blue-600 inline-flex items-center gap-1"><ArrowSquareOut size={14} /></Link>
            </td>
          </tr>
        )}
      />
    </div>
  );
}
